import React from "react";
import styled from "styled-components";
import StatusDot from "./status-dot";
import { PresentationStatus } from "../models/presentationStatus";
import { Colour } from "../models/colour";

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
`;

const Message = styled.span`
  font-size: 14px;
  color: #8c959f;
  line-height: 1;
`;

interface Props {
  status: PresentationStatus;
}

const ConnectionStatus = ({ status }: Props) => {
  return (
    <Wrapper>
      {status === PresentationStatus.ONLINE && (
        <>
          <StatusDot colour={Colour.GREEN} />
          <Message>Connected</Message>
        </>
      )}
      {status === PresentationStatus.CONNECTING && (
        <>
          <StatusDot colour={Colour.YELLOW} />
          <Message>Connecting...</Message>
        </>
      )}
      {status === PresentationStatus.CLOSED && (
        <>
          {/* The clicker retries forever, so a closed socket is always on its
              way back, never a dead end — amber "Reconnecting…" reads truer
              (and far less alarming) than a red "Disconnected" flash when a
              backgrounded/suspended tab drops the connection. */}
          <StatusDot colour={Colour.YELLOW} />
          <Message>Reconnecting…</Message>
        </>
      )}
      {status === PresentationStatus.NOT_STARTED && (
        <>
          {/* Covers both "host hasn't started yet" and "host stepped away" —
              either way the clicker is actively waiting to (re)attach, so an
              amber "waiting" reads truer than a grey "Not Started". */}
          <StatusDot colour={Colour.YELLOW} />
          <Message>Waiting for presentation to start…</Message>
        </>
      )}
      {status === PresentationStatus.EXPIRED && (
        <>
          {/* Dead end (unlike "waiting"): the host's link rotated and can never
              return here, so red + an actionable message, not amber. */}
          <StatusDot colour={Colour.RED} />
          <Message>Link expired — ask the host for a new one</Message>
        </>
      )}
      {status === PresentationStatus.ROOM_FULL && (
        <>
          {/* Another dead end: nothing this person can do from here — only the
              host can open a seat — so red, and the message points at them. */}
          <StatusDot colour={Colour.RED} />
          <Message>Room is full — ask the host to upgrade</Message>
        </>
      )}
      {status === PresentationStatus.REMOVED && (
        <>
          {/* Grey, not red: nothing is broken and they can join again. */}
          <StatusDot colour={Colour.GREY} />
          <Message>Removed by the host</Message>
        </>
      )}
    </Wrapper>
  );
};

export default ConnectionStatus;
